import React, { useState, useMemo } from 'react';
import { CommunityLocation, ComparisonMode } from '../types/api';
import { colorPalette, fontFamily } from '../utils/careColors';
import CommunityCard from './CommunityCard';
import SidebarControls, { SortKey, SortDirection } from './SidebarControls';
import {
  Close
} from '@mui/icons-material';

interface SidebarProps {
  visible: boolean;
  visibleLocations: CommunityLocation[];
  totalLocations: number;
  selectedCommunity?: CommunityLocation | null;
  hoveredCommunityId: string | null;
  filteredCommunity?: CommunityLocation | null;
  onClose: () => void;
  onCommunityClick: (community: CommunityLocation) => void;
  onCommunityHover: (communityId: string | null) => void;
  onClearFilter?: () => void;
  onSearchResultClick?: (result: {
    community_name: string;
    latitude: number;
    longitude: number;
    corporation_name?: string;
  }) => void;
  availableCarelevels?: string[];
  currentCareLevel?: string;
  onCareLevelChange?: (careLevel: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  visible,
  visibleLocations,
  totalLocations,
  selectedCommunity,
  hoveredCommunityId,
  filteredCommunity,
  onClose,
  onCommunityClick,
  onCommunityHover,
  onClearFilter,
  onSearchResultClick,
  availableCarelevels = [],
  currentCareLevel = '',
  onCareLevelChange
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('default');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('radius');

  const sortedLocations = useMemo(() => {
    if (sortKey === 'default') {
      return visibleLocations;
    }

    const sorted = [...visibleLocations].sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case 'qol':
          comparison = (a.qol_last_num || 0) - (b.qol_last_num || 0);
          break;
        case 'qoh':
          comparison = (a.qoh_last_num || 0) - (b.qoh_last_num || 0);
          break;
        case 'los':
          const aLos = a.avg_days_in_residence || a.days_in_residence || 0;
          const bLos = b.avg_days_in_residence || b.days_in_residence || 0;
          comparison = aLos - bLos;
          break;
        case 'name':
          comparison = a.community_name.localeCompare(b.community_name);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [visibleLocations, sortKey, sortDirection]);

  if (!visible) return null;

  return (
    <div style={{
      width: '560px',
      height: '100%',
      backgroundColor: 'white',
      borderRight: '1px solid #ddd',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000
    }}>
      {/* Sidebar Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #eee',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: !filteredCommunity ? '16px' : '0' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', fontFamily, display: 'inline' }}>
              {filteredCommunity ? 'Isolated Community' : 'Communities in View'}
            </h3>
            <span style={{ fontSize: '16px', color: '#666', marginLeft: '8px' }}>
              {filteredCommunity ? '1 of 1' : `${visibleLocations.length} of ${totalLocations.toLocaleString()}`}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {filteredCommunity && onClearFilter && (
              <button
                onClick={onClearFilter}
                style={{
                  background: colorPalette.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontFamily
                }}
                title="Show all communities"
              >
                Show All
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Close fontSize="small" />
            </button>
          </div>
        </div>

        {/* Sidebar Controls (Search, Filter, Sort) */}
        {!filteredCommunity && (
          <SidebarControls
            onSearchResultClick={onSearchResultClick}
            availableCarelevels={availableCarelevels}
            currentCareLevel={currentCareLevel}
            onCareLevelChange={onCareLevelChange}
            sortKey={sortKey}
            onSortKeyChange={setSortKey}
            sortDirection={sortDirection}
            onSortDirectionChange={setSortDirection}
            comparisonMode={comparisonMode}
            onComparisonModeChange={setComparisonMode}
          />
        )}
      </div>

      {/* Sidebar Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '8px'
      }}>

        {sortedLocations.length === 0 ? (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '16px'
          }}>
            No communities visible in current map view.
            <br />
            <span style={{ fontSize: '14px' }}>
              Zoom in or pan to see communities
            </span>
          </div>
        ) : (
          <>
            {/* Render all communities */}
            {sortedLocations.map((community) => (
              <CommunityCard
                key={community.community_id}
                community={community}
                isFiltered={!!filteredCommunity}
                isSelected={selectedCommunity?.community_id === community.community_id}
                onCommunityClick={onCommunityClick}
                onCommunityHover={onCommunityHover}
                currentCareLevel={currentCareLevel}
                comparisonMode={comparisonMode}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
